import Cycle from "@/models/Cycle";
import { registerToPendingCycle } from "@/composables/cycle/registerToPendingCycle";
import { setDisplayedItems } from "@/utils/setDisplayedItems";
import { toDateOnlyString } from "@/utils/dateOnly";
import { list, listAll, retrieve } from "@/utils/factoryRest";
import {
  CycleApi,
  PendingCycleApi,
  PendingCyclePublicApi,
  PublicCycleApi,
} from "@socotec.io/certipers-back-sdk";
import configs from "@/setup/openApiConfig";
import dayjs from "dayjs";
import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";

const cycleRepo = useRepo(Cycle);
const cycleApi = new CycleApi(configs.certipersBackConfig);
const publicCycleApi = new PublicCycleApi(configs.certipersBackConfig);
const pendingCycleApi = new PendingCycleApi(configs.certipersBackConfig);
const pendingCyclePublicApi = new PendingCyclePublicApi(configs.certipersBackConfig);

const formatCycleData = (cycleData) => {
  //INFO - AG 30/05/2023 - Update all Cycles created to link related model Domain.
  return {
    ...cycleData,
    domainVersionUuid: cycleData.domainVersion,
    ...(cycleData.createdAt && {
      createdAt: dayjs(cycleData.createdAt).toISOString(),
    }),
    ...(cycleData.maxEndDate && {
      maxEndDate: toDateOnlyString(cycleData.maxEndDate),
    }),
  };
};

const CYCLE_STORAGE_KEY = "certipers_cycleToRegister";

export const useCycleStore = defineStore("cycle", {
  state: () => ({
    cyclesCount: 0,
    cycleToRegister: JSON.parse(localStorage.getItem(CYCLE_STORAGE_KEY) || "null"),
    pendingCycleUuid: null,
  }),

  actions: {
    fetchPublicCycles: list({
      ModelRepo: cycleRepo,
      restListMethod: publicCycleApi.list.bind(publicCycleApi),
      handleResponseData: ({ results }) => {
        return results.map(formatCycleData);
      },
      onItemsInserted: (responseData) => {
        useCycleStore().setCyclesCount(responseData.count);
        setDisplayedItems(cycleRepo, responseData.results);
      },
    }),

    fetchCycles: list({
      ModelRepo: cycleRepo,
      restListMethod: cycleApi.list.bind(cycleApi),
      handleResponseData: ({ results }) => {
        return results.map(formatCycleData);
      },
      onItemsInserted: (responseData) => {
        setDisplayedItems(cycleRepo, responseData.results);
        useCycleStore().setCyclesCount(responseData.count);
      },
    }),

    async fetchAllCycles({ filters = {} } = {}) {
      const results = await listAll(cycleApi.list.bind(cycleApi), filters);

      this.setCyclesCount(results.length);
      const cycles = cycleRepo.save(results.map(formatCycleData));
      setDisplayedItems(cycleRepo, results);

      return cycles;
    },

    async fetchPendingCycle({ userUuid }) {
      const response = await pendingCycleApi.list({ userUuid: [userUuid] });

      if (!response.results.length) {
        return;
      } else {
        const { lastPendingCycle } = registerToPendingCycle(this);
        lastPendingCycle(response.results[0]);
        this.setPendingCycleUuid(response.results[0].uuid);
      }
    },

    async destroyPendingCycle() {
      await pendingCycleApi.destroy({ uuid: this.getPendingCycleUuid });
      this.resetCycleToRegister();
    },

    async createPendingCycle(user_uuid) {
      await pendingCyclePublicApi.create({
        pendingCycleRequest: {
          user: user_uuid,
          cycle: this.getCycleToRegister.uuid,
        },
      });
    },

    retrieveCycle: retrieve({
      ModelRepo: cycleRepo,
      restRetrieveMethod: cycleApi.retrieve.bind(cycleApi),
      handleResponseData: (response) => {
        return formatCycleData(response);
      },
    }),

    setCyclesCount(count) {
      this.cyclesCount = count;
    },

    setCycleToRegister(cycle) {
      this.cycleToRegister = cycle;
      if (cycle) {
        localStorage.setItem(CYCLE_STORAGE_KEY, JSON.stringify(cycle));
      } else {
        localStorage.removeItem(CYCLE_STORAGE_KEY);
      }
    },

    setPendingCycleUuid(pendingCycle_uuid) {
      this.pendingCycleUuid = pendingCycle_uuid;
    },

    resetCycleToRegister() {
      this.cycleToRegister = null;
      localStorage.removeItem(CYCLE_STORAGE_KEY);
    },
  },

  getters: {
    getCyclesCount: (state) => state.cyclesCount,
    getAllCyclesDisplayed: () => {
      return cycleRepo
        .where("isDisplayed", true)
        .with("domainVersionData", (query) => query.with("domainData"))
        .with("stepsList")
        .get();
    },
    getPendingCycleUuid: (state) => state.pendingCycleUuid,
    getCycleDisplayed: () => {
      return cycleRepo
        .where("isDisplayed", true)
        .with("domainVersionData", (query) => query.with("domainData"))
        .first();
    },
    getCycleToRegister: (state) => state.cycleToRegister,
    getCycleToRegisterWithDomainData: (state) => {
      if (!state.cycleToRegister) {
        return;
      } else {
        const cycle = {
          ...state.cycleToRegister,
          ...cycleRepo
            .with("domainVersionData", (query) => query.with("domainData"))
            .with("stepsList", (query) => {
              query.where("order", 1);
            })
            .whereId(state.cycleToRegister.uuid)
            .first(),
        };
        return cycle;
      }
    },
    getAllActiveCycles: () => {
      return cycleRepo
        .with("domainVersionData", (query) => query.with("domainData"))
        .with("stepsList")
        .whereHas("domainVersionData", (query) => {
          query.where("isActive", true);
        })
        .get();
    },
  },
});
